import NotFound from './NotFound';
import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle, Phone } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import PageHero from '../components/PageHero';
import Seo from '../components/Seo';
import TextNowLink from '../components/TextNowLink';
import { CONTACT, SERVICES } from '../data/siteData';
import { SERVICE_CONTENT } from '../data/serviceContent';
import { ORGANIZATION_ID, SITE_URL, breadcrumbSchema } from '../lib/seoData';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((item) => item.slug === slug);

  if (!service) return <NotFound />;
  const Icon = service.icon;
  const content = SERVICE_CONTENT[service.slug];
  const pagePath = `/services/${service.slug}`;
  const requestPath = `/contact?service=${encodeURIComponent(service.slug)}#service-request`;
  const seoTitle = `${service.title} Richmond BC`;
  const seoDescription = `${service.short} Call Jonny's Towing in Richmond, BC to check availability and pricing.`;
  const structuredData = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/services' },
      { name: service.title, path: pagePath },
    ]),
    {
      '@type': 'Service',
      '@id': `${SITE_URL}${pagePath}#service`,
      name: service.title,
      serviceType: service.title,
      description: content.details,
      url: `${SITE_URL}${pagePath}`,
      provider: { '@id': ORGANIZATION_ID },
      areaServed: [
        { '@type': 'City', name: 'Richmond, British Columbia' },
        { '@type': 'AdministrativeArea', name: 'Greater Vancouver, British Columbia' },
      ],
    },
  ];

  return (
    <>
      <Seo title={seoTitle} description={seoDescription} canonicalPath={pagePath} structuredData={structuredData} />
      <PageHero eyebrow="Richmond, BC towing and roadside assistance" title={`${service.title} in Richmond, BC`} description={service.short} image="/images/flatbed-sunset.webp">
        <a className="button button--primary" href={CONTACT.phoneHref} data-roka-cta="call_dispatch" data-roka-label="Call dispatch"><Phone size={18} /> Call for service</a>
        <TextNowLink className="button button--ghost"><MessageCircle size={18} /> Text Now</TextNowLink>
        <Link className="button button--ghost" to={requestPath} data-roka-cta={`request_${service.slug}`} data-roka-label={`Request ${service.title}`}><ArrowRight size={18} /> Request this service</Link>
      </PageHero>
      <section className="section container service-detail-grid">
        <article className="prose-copy">
          <span className="service-detail-icon"><Icon size={34} /></span>
          <h2>{content.heading}</h2>
          <p className="lead">{content.lead}</p>
          <p>{content.details}</p>
          <h3>When this service can help</h3>
          <ul className="check-list">
            {content.scenarios.map((scenario) => <li key={scenario}><CheckCircle2 /> {scenario}</li>)}
          </ul>
          <h3>Preparing the right response</h3>
          <p>{content.prep}</p>
          <h3>Common questions</h3>
          <div className="service-faq">
            {content.faqs.map(({ question, answer }) => (
              <div className="service-faq__item" key={question}>
                <h4>{question}</h4>
                <p>{answer}</p>
              </div>
            ))}
          </div>
          <p>Pricing and availability depend on the vehicle, the location, access, distance, safety and the equipment needed. Dispatch will confirm the options before a job is booked.</p>
          <Link className="text-link" to="/services"><ArrowLeft size={17} /> Back to all services</Link>
        </article>
        <aside className="contact-card">
          <span className="eyebrow">Need this service?</span>
          <h3>Call or text Jonny’s Towing.</h3>
          <p>For the fastest assessment, include your location and vehicle details.</p>
          <a className="button button--primary" href={CONTACT.phoneHref} data-roka-cta="call_dispatch" data-roka-label="Call dispatch"><Phone size={18} /> {CONTACT.phoneDisplay}</a>
          <TextNowLink className="button button--secondary"><MessageCircle size={18} /> Text Now</TextNowLink>
          <Link className="button button--ghost" to={requestPath} data-roka-cta={`request_${service.slug}`} data-roka-label={`Request ${service.title}`}><ArrowRight size={18} /> Request this service</Link>
        </aside>
      </section>
    </>
  );
}
